import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

export default function ChangePassword() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ old_password: "", new_password: "", confirm: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const isAdmin = user && user.role !== "client";
  const homePath = isAdmin ? "/admin" : "/portal";

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.new_password.length < 8) { setErr("New password must be at least 8 characters."); return; }
    if (f.new_password !== f.confirm) { setErr("New password and confirmation don't match."); return; }
    if (f.new_password === f.old_password) { setErr("New password must be different from the current one."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: f.old_password, new_password: f.new_password });
      toast.success("Password changed. Please sign in again with your new password.");
      logout();
      nav(isAdmin ? "/admin/login" : "/login", { replace: true });
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (Array.isArray(d) && d[0]?.msg) ? d[0].msg : "Could not change password");
    } finally { setBusy(false); }
  };

  if (!user) { nav("/login", { replace: true }); return null; }

  return (
    <div className="min-h-screen" style={{ background: "var(--cream)" }} data-testid="change-password-root">
      <header className="border-b-2" style={{ borderColor: "var(--ink)" }}>
        <div className="max-w-[860px] mx-auto px-6 flex justify-between items-center py-4">
          <Link to={homePath} className="text-2xl font-display font-black" data-testid="cp-brand">
            Ask<span style={{ color: "var(--kiwi)" }}>Tax</span>
          </Link>
          <Link to={homePath} className="text-sm font-display font-bold uppercase tracking-wide flex items-center gap-2" data-testid="cp-back">
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </header>

      <section className="py-16">
        <div className="max-w-[520px] mx-auto px-6">
          <span className="eyebrow">Account security</span>
          <h1 className="h1-display mt-3" style={{ fontSize: "clamp(36px,5vw,52px)" }}>Change <em className="font-serif-i font-normal" style={{ textTransform: "none", color: "var(--kiwi)" }}>password</em></h1>
          <p className="mt-4 font-serif italic" style={{ color: "var(--muted)" }}>
            Signed in as <b>{user.email}</b>. You'll be signed out after changing it — sign back in with the new password.
          </p>

          <form onSubmit={submit} className="at-card p-8 mt-8">
            {err && <div className="mb-4 p-3 border-2 text-sm font-display font-bold uppercase tracking-wide" style={{ borderColor: "var(--clay)", background: "#f4e0d8", color: "var(--clay)" }} data-testid="cp-error">{err}</div>}

            <div>
              <label className="at-label">Current password</label>
              <input required type="password" className="at-input" value={f.old_password} onChange={e => setF({ ...f, old_password: e.target.value })} data-testid="cp-old" autoComplete="current-password" />
            </div>
            <div className="mt-4">
              <label className="at-label">New password</label>
              <input required type="password" className="at-input" minLength={8} value={f.new_password} onChange={e => setF({ ...f, new_password: e.target.value })} data-testid="cp-new" autoComplete="new-password" />
              <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>At least 8 characters. A passphrase of 4 unrelated words is stronger and easier to remember than a short password with symbols.</div>
            </div>
            <div className="mt-4">
              <label className="at-label">Confirm new password</label>
              <input required type="password" className="at-input" minLength={8} value={f.confirm} onChange={e => setF({ ...f, confirm: e.target.value })} data-testid="cp-confirm" autoComplete="new-password" />
            </div>
            <button disabled={busy} className="at-btn block mt-6" data-testid="cp-submit">
              <KeyRound size={16} /> {busy ? "Updating…" : "Change password"}
            </button>
            <div className="mt-4 flex items-center gap-2 text-xs" style={{ color: "var(--muted)" }}>
              <ShieldCheck size={13} /> We store passwords as bcrypt hashes — even we can't read them.
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
